import React, { useEffect, useRef, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { Button, Spin, Tooltip } from 'antd';
import { ReloadOutlined, AimOutlined, UpOutlined, DownOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import FullScreen from '@/common/components/business/FullScreen';
import { defaultMapStyle } from '@/layout/ts-config';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Empty from '@/common/components/Data/V2Empty';
import V2AMap from '@/common/components/Map/V2AMap';
import { MallData } from '../../types';
import styles from './index.module.less';

// 2026-1029迭代/商场慢闪场景体验优化（文档：https://confluence.lanhanba.com/pages/viewpage.action?pageId=172108153）：仅本报告禁止手势驱动地图，保留高德键盘方向键和工具条缩放。

/**
 * 地图交互方案（开发人员开关，不对外暴露交互入口）
 * - pad：方向键盘移动，禁用地图手势
 * - gesture：恢复高德原生手势拖动/缩放，隐藏方向键盘
 * 断言避免字面量被收窄成单一类型，否则 pad/gesture 分支判断会被 TS 判为恒假
 */
export const MAP_INTERACTION_MODE = 'pad' as 'pad' | 'gesture';

interface IndoorLayer {
  showIndoorMap: (poiId: string, floor?: number) => void;
  showFloorBar: () => void;
  getSelectedBuildingId: () => string;
}
interface IndoorMapInstance {
  setZoomAndCenter: (zoom: number, center: [number, number]) => void;
  panBy?: (x: number, y: number, duration?: number) => void;
  addControl: (control: any) => void;
  indoorMap?: IndoorLayer;
  on: (event: string, callback: () => void) => void;
  off: (event: string, callback: () => void) => void;
}
const targetZoom = 18;

/** 方向键模式禁止手势驱动地图，手势模式交还给高德原生手势 */
const getGestureOpts = (mode: 'pad' | 'gesture') => {
  const usingPad = mode === 'pad';
  return {
    dragEnable: !usingPad,
    touchZoom: !usingPad,
    doubleClickZoom: !usingPad,
    keyboardEnable: true,
    rotateEnable: false,
    pitchEnable: false,
  };
};

const MallFloorMap: React.FC<{ data: MallData['indoor'] }> = ({ data }) => {
  const [status, setStatus] = useState<'loading' | 'ready' | 'unavailable'>('loading');
  const [attempt, setAttempt] = useState(0);
  const mapRef = useRef<IndoorMapInstance | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number>();
  const startedRef = useRef(0);
  const selectedRef = useRef<MallData['indoor']>();
  const requestRef = useRef(0);
  const methods = useMethods({
    showIndoor() {
      const selected = selectedRef.current;
      if (!selected?.poiId || !mapRef.current?.indoorMap) return;
      try {
        mapRef.current.indoorMap.showIndoorMap(selected.poiId, selected.floor || 1);
        mapRef.current.indoorMap.showFloorBar();
        setStatus('ready');
        window.clearInterval(timerRef.current);
      } catch {
        setStatus('unavailable');
      }
    },
    loaded(map: IndoorMapInstance) {
      mapRef.current = map;
      // Same scale placement as YouthMap/AreaDetailDrawer/FirstScreenMap.
      map.addControl(new window.AMap.Scale({ position: 'RB', offset: [12, 8] }));
      map.addControl(new window.AMap.ToolBar({ position: 'RT', offset: [12, 12] }));
      map.on('indoor_create', methods.showIndoor);
      selectedRef.current = data;
      if (data?.poiId) methods.showIndoor();
      else {
        // 直接依赖高德默认室内图层按坐标识别商场，避免 POI 反查偏移。
        const center = selectedRef.current?.center;
        if (center) map.setZoomAndCenter(targetZoom, center);
        map.indoorMap?.showFloorBar();
        setStatus('ready');
        window.clearInterval(timerRef.current);
      }
    },
    searchMall() {
      if (!data) return;
      const version = ++requestRef.current;
      const search = new window.AMap.PlaceSearch({ type: '060100', pageSize: 1, pageIndex: 1, sortrule: 1 });
      search.searchNearBy('', data.center, 50000, (searchStatus: string, result: any) => {
        if (version !== requestRef.current || !mapRef.current) return;
        const poi = searchStatus === 'complete' ? result?.poiList?.pois?.[0] : undefined;
        if (!poi?.id || !poi.location) {
          setStatus('unavailable');
          window.clearInterval(timerRef.current);
          return;
        }
        selectedRef.current = { poiId: poi.id, floor: 1, center: [poi.location.lng, poi.location.lat] };
        startedRef.current = Date.now();
        methods.recenter();
      });
    },
    checkCoverage() {
      if (selectedRef.current?.poiId && mapRef.current?.indoorMap?.getSelectedBuildingId() === selectedRef.current.poiId) {
        setStatus('ready');
        window.clearInterval(timerRef.current);
      } else if (Date.now() - startedRef.current >= 15000) {
        setStatus('unavailable');
        requestRef.current += 1;
        window.clearInterval(timerRef.current);
      }
    },
    cleanup() {
      requestRef.current += 1;
      window.clearInterval(timerRef.current);
      mapRef.current?.off('indoor_create', methods.showIndoor);
      mapRef.current = null;
    },
    initialize() {
      setStatus('loading');
      startedRef.current = Date.now();
      if (data) timerRef.current = window.setInterval(methods.checkCoverage, 300);
      return methods.cleanup;
    },
    retry() { setAttempt(attempt + 1); },
    recenter() {
      if (!selectedRef.current) return;
      mapRef.current?.setZoomAndCenter(targetZoom, selectedRef.current.center);
      methods.showIndoor();
    },
    panUp() { mapRef.current?.panBy?.(0, -80, 200); },
    panDown() { mapRef.current?.panBy?.(0, 80, 200); },
    panLeft() { mapRef.current?.panBy?.(-80, 0, 200); },
    panRight() { mapRef.current?.panBy?.(80, 0, 200); },
  });
  useEffect(methods.initialize, [data?.poiId, data?.floor, data?.center[0], data?.center[1], attempt]);

  return <div className={styles.root}>
    <V2Title type='H2' text='商场楼层品牌导览' bold />
    <div className={styles.map} ref={containerRef}>
      {data ? <>
        <V2AMap key={`${data.poiId}-${data.center.join(',')}-${data.floor}-${attempt}`} height='100%' plugins={['AMap.Scale', 'AMap.IndoorMap', 'AMap.ToolBar']} loaded={methods.loaded}
          mapOpts={{ center: data.center, zoom: targetZoom, zooms: [13.5, 20], showIndoorMap: true, mapStyle: `amap://styles/${defaultMapStyle}`, ...getGestureOpts(MAP_INTERACTION_MODE), scrollWheel: MAP_INTERACTION_MODE === 'gesture' }} />
        {MAP_INTERACTION_MODE === 'pad' && <div className={styles.directionPad}>
          <div className={styles.directionControls} role='group' aria-label='地图方向控制'>
            <Button aria-label='地图向上' icon={<UpOutlined />} onClick={methods.panUp} />
            <Button aria-label='地图向左' icon={<LeftOutlined />} onClick={methods.panLeft} />
            <Button aria-label='地图向右' icon={<RightOutlined />} onClick={methods.panRight} />
            <Button aria-label='地图向下' icon={<DownOutlined />} onClick={methods.panDown} />
          </div>
        </div>}
        <div className={styles.controls}>
          <Tooltip title='回到商场中心'><Button className={styles.recenter} aria-label='回到商场中心' icon={<AimOutlined />} onClick={methods.recenter} /></Tooltip>
          <FullScreen containerRef={containerRef} />
        </div>
        {status === 'loading' && <div className={styles.overlay}><Spin /></div>}
        {status === 'unavailable' && <div className={styles.overlay}>
          <V2Empty customTip='暂未获取到该商场的楼层地图' />
          <Button icon={<ReloadOutlined />} onClick={methods.retry}>重试</Button>
        </div>}
      </> : <V2Empty centerInBlock customTip='暂无商场楼层地图' />}
    </div>
  </div>;
};
export default MallFloorMap;
